/* ==========================================================================
   Verhalen-raster: kaarten komen gestaffeld omhoog en faden in

   ScrollTrigger.batch verzamelt de kaarten die tegelijk in beeld komen, dus
   een rij van drie komt als één gestaffelde golf en een tweede rij begint pas
   als hij zelf in beeld is. Eenmalig: scrol je terug, dan blijven de kaarten
   staan.

   Geen animatie (en dus meteen de eindstand) bij: geen data-vr-animatie,
   verminderde beweging, of een GSAP dat niet laadt. data-vr-klaar haalt de
   verberg-regel uit verhalen-raster.css weg.

   Ruimt zichzelf op bij verwijderen (shopify:section:unload) en start
   opnieuw bij terugplaatsen (shopify:section:load): zie wereld-hero.js.
   ========================================================================== */

/* Eén keer per pagina uitgevoerd in een eigen scope: een sectie die twee keer
   op de pagina staat laadt dit bestand twee keer, en een klasse op het niveau
   van het bestand zou dan een fout geven. */
(() => {
  class VerhalenRaster extends HTMLElement {
    connectedCallback() {
      this.gestart = false;
      this.stopVoorkeur = window.elGsap?.opVoorkeurWijziging(() => this.opnieuw());
      // Over de grens van 990 px wisselt de scroller (zie scroller()): opnieuw opbouwen
      this.breedte = window.matchMedia('(min-width: 990px)');
      this.naBreedte = () => this.opnieuw();
      this.breedte.addEventListener('change', this.naBreedte);
      this.startRail();
      this.start();
    }

    disconnectedCallback() {
      this.stopVoorkeur?.();
      this.breedte?.removeEventListener('change', this.naBreedte);
      this.railStop?.abort();
      this.opruimen();
    }

    /* Mobiele swipe-rail: dit deel heeft geen GSAP nodig. Het houdt de
       voortgangslijn bij en maakt de rail focusbaar zodra hij echt scrolt, zodat
       een toetsenbordgebruiker hem met de pijltoetsen kan bedienen. Op desktop
       is het een raster dat niet scrolt: dan blijft alles verborgen. */
    startRail() {
      const rail = this.querySelector('[data-vr-rail]');
      const voortgang = this.querySelector('[data-vr-voortgang]');
      const balk = this.querySelector('[data-vr-balk]');
      if (!rail || !voortgang || !balk) return;

      this.railStop = new AbortController();
      const { signal } = this.railStop;
      let wacht = false;

      const werkBij = () => {
        wacht = false;
        const ruimte = rail.scrollWidth - rail.clientWidth;
        const scrolt = ruimte > 4;
        voortgang.hidden = !scrolt;
        if (scrolt) rail.setAttribute('tabindex', '0');
        else rail.removeAttribute('tabindex');
        if (!scrolt) return;
        const deel = rail.clientWidth / rail.scrollWidth;
        const spoor = voortgang.clientWidth;
        const vooruit = rail.scrollLeft / ruimte;
        balk.style.setProperty('--vr-deel', deel.toFixed(3));
        balk.style.setProperty('--vr-voortgang', `${(spoor * (1 - deel) * vooruit).toFixed(1)}px`);
      };
      const plan = () => {
        if (!wacht) {
          wacht = true;
          requestAnimationFrame(werkBij);
        }
      };

      rail.addEventListener('scroll', plan, { passive: true, signal });
      window.addEventListener('resize', plan, { signal });
      signal.addEventListener('abort', () => {
        rail.removeAttribute('tabindex');
        voortgang.hidden = true;
      });
      werkBij();
    }

    /* Vanaf 990 px scrolt in dit thema .page-wrapper en niet het venster
       (base.css): ScrollTrigger moet dat element als scroller krijgen, anders
       reageert hij nooit. Onder 990 px scrolt het venster. */
    scroller() {
      const pagina = document.querySelector('.page-wrapper');
      return pagina && /(auto|scroll)/.test(getComputedStyle(pagina).overflowY) ? pagina : window;
    }

    opnieuw() {
      this.opruimen();
      this.start();
    }

    opruimen() {
      this.gestart = false;
      this.afbeeldingen?.abort();
      this.afbeeldingen = null;
      // revert() draait ook de batch van ScrollTrigger en de inline stijlen terug
      this.context?.revert();
      this.context = null;
    }

    async start() {
      if (this.gestart) return;
      this.gestart = true;

      const kaarten = this.querySelectorAll('[data-vr-kaart]');
      const uitgezet = !this.hasAttribute('data-vr-animatie');
      if (uitgezet || !kaarten.length || !window.elGsap || window.elGsap.verminderdeBeweging()) {
        this.setAttribute('data-vr-klaar', '');
        return;
      }

      try {
        const { gsap, ScrollTrigger } = await window.elGsap.laad(this, ['ScrollTrigger']);
        if (!this.isConnected || !this.gestart) return;
        if (!ScrollTrigger) throw new Error('ScrollTrigger ontbreekt');
        const scroller = this.scroller();

        this.context = gsap.context(() => {
          gsap.set(kaarten, { opacity: 0, y: 44 });
          this.setAttribute('data-vr-klaar', '');

          ScrollTrigger.batch(kaarten, {
            scroller,
            start: 'top 88%',
            once: true,
            onEnter: (batch) =>
              gsap.to(batch, {
                opacity: 1,
                y: 0,
                duration: 0.95,
                ease: 'power3.out',
                stagger: 0.14,
                overwrite: true,
                clearProps: 'transform,opacity',
              }),
          });
        }, this);

        /* Beelden die pas later laden veranderen de hoogte van de sectie niet
           (vaste verhouding), maar de pagina erboven kan nog verschuiven:
           een refresh na het laden zet de triggers op de juiste plek. */
        this.afbeeldingen = new AbortController();
        const { signal } = this.afbeeldingen;
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', () => ScrollTrigger.refresh(), { once: true, signal });
        });
      } catch {
        // Mislukt het laden, dan staan de kaarten gewoon op hun eindstand
        this.setAttribute('data-vr-klaar', '');
      }
    }
  }

  if (!customElements.get('verhalen-raster')) {
    customElements.define('verhalen-raster', VerhalenRaster);
  }
})();
